import { NotificationDraft, RecipientGroup } from './types'
import { toTwentyFourHour } from './form-helpers'

// Recipient phrasing for the card detail line, per the cadence design
// ("On event · Send to all participants", "Send to skip-level manager", …).
export const RECIPIENT_DETAIL_LABELS: Record<RecipientGroup, string> = {
  all_participants: 'all participants',
  individual_contributor: 'individual contributor',
  managers: 'manager',
  skip_level_managers: 'skip-level manager',
  additional_recipients: 'additional recipients',
}

// Title-case labels for the modal's recipient checkboxes.
export const RECIPIENT_GROUP_LABELS: Record<RecipientGroup, string> = {
  all_participants: 'All Participants',
  individual_contributor: 'Individual Contributor',
  managers: 'Managers',
  skip_level_managers: 'Skip-level Managers',
  additional_recipients: 'Additional Recipients',
}

// The four groups offered as checkboxes in the modal. individual_contributor isn't
// a separate picker option (at broadcast level it resolves like all_participants).
export const RECIPIENT_GROUP_OPTIONS: Array<{
  value: RecipientGroup
  label: string
}> = [
  { value: 'all_participants', label: RECIPIENT_GROUP_LABELS.all_participants },
  { value: 'managers', label: RECIPIENT_GROUP_LABELS.managers },
  {
    value: 'skip_level_managers',
    label: RECIPIENT_GROUP_LABELS.skip_level_managers,
  },
  {
    value: 'additional_recipients',
    label: RECIPIENT_GROUP_LABELS.additional_recipients,
  },
]

// "{timing} · Send to {recipients}" — e.g. "On event · Send to all participants",
// "3 days after · Send to skip-level manager".
export function notificationDetail(draft: NotificationDraft): string {
  const dayLabel = draft.offsetDays === 1 ? 'day' : 'days'
  const beforeOrAfter =
    draft.offsetDirection === 'before_event' ? 'before' : 'after'
  const timing =
    draft.offsetDirection === 'on_event'
      ? 'On event'
      : `${draft.offsetDays} ${dayLabel} ${beforeOrAfter}`

  const who =
    draft.recipientGroups.length === 0
      ? 'no recipients'
      : draft.recipientGroups
          .map((group) => RECIPIENT_DETAIL_LABELS[group])
          .join(' + ')

  return `${timing} · Send to ${who}`
}

// Serialize a draft into the snake_case attributes the Save endpoint expects.
// `sendTime` is held as a "9 AM" label in the working state and converted to the
// "HH:MM" the time column parses. `position` comes from array order.
export function serializeNotification(
  draft: NotificationDraft,
  index: number
): Record<string, unknown> {
  const usesOffset = draft.offsetDirection !== 'on_event'

  return {
    id: draft.id,
    kind: draft.kind,
    name: draft.name.trim(),
    anchor: draft.anchor,
    offset_direction: draft.offsetDirection,
    offset_days: usesOffset ? draft.offsetDays : 0,
    send_time: usesOffset ? toTwentyFourHour(draft.sendTime ?? '9 AM') : null,
    send_timezone: usesOffset ? draft.sendTimezone : null,
    recipient_groups: draft.recipientGroups,
    additional_emails: draft.recipientGroups.includes('additional_recipients')
      ? draft.additionalEmails
      : [],
    email_subject: draft.emailSubject,
    email_body: draft.emailBody,
    enabled: draft.enabled,
    position: index,
  }
}

// A blank draft for the "Add another reminder" flow. New custom reminders start
// DISABLED — the admin enables them afterwards (per the design).
export function blankNotificationDraft(): NotificationDraft {
  return {
    id: '',
    kind: 'custom',
    name: '',
    anchor: 'due',
    offsetDirection: 'on_event',
    offsetDays: 0,
    sendTime: '9 AM',
    sendTimezone: null,
    recipientGroups: ['all_participants'],
    additionalEmails: [],
    emailSubject: '',
    emailBody: '',
    enabled: false,
    position: 0,
  }
}
